import { motion, AnimatePresence, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import {
  Repeat,
  Users,
  MessageSquare,
  Smartphone,
  ShieldCheck,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const STEP_DURATION_MS = 6000;

const benefits = [
  {
    icon: Repeat,
    label: "Fidélisation",
    title: "Vos clients reviennent — automatiquement",
    stat: "2 modes",
    statLabel: "visites · tickets de caisse",
    body:
      "Chaque visite ajoute un tampon, ou chaque ticket cumule un montant — vous choisissez le mode. Au seuil que vous fixez, le client débloque la récompense : produit offert, remise ou geste maison. Plus de carte papier, plus rien à compter au comptoir.",
  },
  {
    icon: Users,
    label: "Connaissance client",
    title: "Votre première vraie base de données client",
    stat: "100%",
    statLabel: "des visites tracées",
    body:
      "Chaque inscription consentie crée un profil : numéro vérifié, fréquence, dernier passage, total de tampons. Repérez vos vrais habitués, ceux qui décrochent, ceux qu'une attention ramènerait. Un tableau de bord simple et exportable — le vôtre.",
  },
  {
    icon: MessageSquare,
    label: "Marketing direct",
    title: "Le SMS qui ramène le client absent",
    stat: "98%",
    statLabel: "de taux d'ouverture SMS",
    body:
      "Promotion du jour, nouveau menu, relance d'un client absent depuis 3 semaines : un SMS ciblé à ceux qui ont consenti. Segmentez par fréquence, récompenses débloquées ou dernière visite. Au Maroc, le SMS bat l'email à plate couture.",
  },
  {
    icon: Smartphone,
    label: "Zéro friction",
    title: "Aucune application. Vraiment aucune.",
    stat: "0",
    statLabel: "seconde d'installation client",
    body:
      "Le client scanne le QR avec son appareil photo : sa carte apparaît aussitôt dans son navigateur, sur iPhone comme sur Android. Un tap pour l'ajouter à l'écran d'accueil, ou rien du tout. Aucun App Store, aucune permission, aucune mise à jour.",
  },
  {
    icon: ShieldCheck,
    label: "Conformité légale",
    title: "Couvert sans avoir à comprendre la loi",
    stat: "CNDP",
    statLabel: "déclaration gérée pour vous",
    body:
      "Consentement explicite, journal d'audit signé, déclaration CNDP en notre nom, droit à l'oubli en un clic, suppression automatique après inactivité : tout est géré. Vous signez un contrat de traitement standard — pas besoin d'avocat ni de DPO.",
  },
  {
    icon: TrendingUp,
    label: "Plus de revenus",
    title: "+67% de revenus par client fidèle",
    stat: "+67%",
    statLabel: "vs un client occasionnel",
      body:
      "Un client régulier dépense en moyenne 67 % de plus qu'un nouveau. Votre programme devient un actif qui rapporte, pas un coût marketing : le programme est gratuit, et votre seul coût — les campagnes que vous choisissez d'envoyer — est vite compensé par la valeur à vie de chaque client qu'elles font revenir au bon moment.",
  },
];

export const Benefits = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.15 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isInView || isPaused) return;
    const t = setTimeout(() => {
      setActiveIndex((i) => (i + 1) % benefits.length);
    }, STEP_DURATION_MS);
    return () => clearTimeout(t);
  }, [activeIndex, isPaused, isInView]);

  const active = benefits[activeIndex];
  const ActiveIcon = active.icon;
  const goPrev = () =>
    setActiveIndex((i) => (i - 1 + benefits.length) % benefits.length);
  const goNext = () => setActiveIndex((i) => (i + 1) % benefits.length);

  return (
    <section id="avantages" ref={ref} className="relative py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/[0.03] rounded-full blur-[120px] -translate-y-1/3 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/[0.04] rounded-full blur-[100px] translate-y-1/3 -translate-x-1/3 pointer-events-none" />

      <div className="container relative mx-auto px-4 lg:px-6 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-primary/8 text-primary border border-primary/15 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            Pourquoi Medina Fidélité
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mb-4">
            Six raisons concrètes,{" "}
            <span className="text-gradient">une à la fois</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ce que chacune change pour votre commerce, concrètement.
          </p>
        </motion.div>

        <div className="relative">
          {/* Spotlight stage */}
          <div
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="relative overflow-hidden rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/[0.05] via-card to-accent/[0.03] min-h-[420px] md:min-h-[380px]"
          >
            <motion.div
              animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.5, 0.25] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/15 blur-[100px] pointer-events-none"
            />
            <motion.div
              animate={{ scale: [1, 1.1, 1], opacity: [0.15, 0.35, 0.15] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-accent/15 blur-[90px] pointer-events-none"
            />

            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -30, filter: "blur(10px)" }}
                transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="relative grid md:grid-cols-[auto_1fr] gap-8 lg:gap-14 items-center p-8 lg:p-14"
              >
                {/* Stat side */}
                <div className="text-center md:text-left">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.15, type: "spring", stiffness: 200 }}
                    className="inline-flex items-center justify-center rounded-2xl bg-primary/15 p-4 mb-5 shadow-lg shadow-primary/20"
                  >
                    <ActiveIcon className="h-8 w-8 text-primary" />
                  </motion.div>
                  <p className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-3">
                    {active.label}
                  </p>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.25 }}
                    className="text-6xl sm:text-7xl lg:text-8xl font-extrabold text-gradient leading-none tracking-tight mb-3"
                  >
                    <bdi>{active.stat}</bdi>
                  </motion.div>
                  <p className="text-sm text-muted-foreground max-w-[16rem] md:mx-0 mx-auto">
                    {active.statLabel}
                  </p>
                </div>

                {/* Body side */}
                <div>
                  <motion.h3
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-2xl lg:text-3xl font-bold text-foreground mb-4 tracking-tight leading-tight"
                  >
                    {active.title}
                  </motion.h3>
                  <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-base text-muted-foreground leading-relaxed"
                  >
                    {active.body}
                  </motion.p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Prev / Next chevrons (desktop) */}
            <button
              onClick={goPrev}
              aria-label="Avantage précédent"
              className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm border border-border/60 text-muted-foreground hover:text-primary hover:border-primary/30 hover:bg-background transition-all"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={goNext}
              aria-label="Avantage suivant"
              className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm border border-border/60 text-muted-foreground hover:text-primary hover:border-primary/30 hover:bg-background transition-all"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Progress bars */}
          <div className="mt-6 flex gap-1.5">
            {benefits.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                aria-label={`Avantage ${i + 1} : ${benefits[i].label}`}
                className="group flex-1 h-1.5 rounded-full overflow-hidden bg-border/60 relative cursor-pointer hover:bg-border transition-colors"
              >
                {i === activeIndex && (
                  <motion.div
                    key={activeIndex}
                    initial={{ width: "0%" }}
                    animate={{ width: isPaused ? "100%" : "100%" }}
                    transition={{
                      duration: isPaused ? 0 : STEP_DURATION_MS / 1000,
                      ease: "linear",
                    }}
                    className="absolute inset-y-0 left-0 bg-primary"
                  />
                )}
                {i < activeIndex && (
                  <div className="absolute inset-y-0 left-0 right-0 bg-primary/30" />
                )}
              </button>
            ))}
          </div>

          {/* Thumbnail row — clickable mini cards */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              const isActive = i === activeIndex;
              return (
                <motion.button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  whileHover={{ y: -2 }}
                  className={`group relative rounded-xl border p-4 text-left transition-all ${
                    isActive
                      ? "border-primary/40 bg-primary/5 shadow-md shadow-primary/10"
                      : "border-border/60 bg-card hover:border-primary/25"
                  }`}
                >
                  <div
                    className={`inline-flex items-center justify-center rounded-lg p-2 mb-2 transition-colors ${
                      isActive ? "bg-primary/15" : "bg-primary/5 group-hover:bg-primary/10"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 transition-colors ${
                        isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                      }`}
                    />
                  </div>
                  <p
                    className={`text-xs font-bold leading-tight transition-colors ${
                      isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"
                    }`}
                  >
                    {b.label}
                  </p>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
